import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Search, Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiGet, apiPost } from '@/lib/api';
import { showToast } from './Toast';
import { useAuthStore } from '@/stores';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  isDeveloper?: boolean;
  isProjectMember?: boolean;
}
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  conversationTitle: string;
  projectId?: string | null;
}
export default function ShareModal({
  isOpen,
  onClose,
  conversationId,
  conversationTitle,
  projectId,
}: ShareModalProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedDescription, setSharedDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedUsersRef = useRef(false);
  // 新增：项目成员的 id 集合
  const [projectMemberIds, setProjectMemberIds] = useState<Set<string>>(
    new Set()
  );
  // Get current user info
  const { userInfo } = useAuthStore();
  const currentUserId = userInfo?.id;
  // 新增：获取项目成员列表
  const loadProjectMembers = useCallback(async () => {
    if (!projectId) {
      setProjectMemberIds(new Set());
      return;
    }

    try {
      const url = `/api/projects/${projectId}/users`;

      const response = await apiGet<any>(url);

      const data = response?.data || response || {};
      const members = data.users || data || [];

      // 创建项目成员 id 的集合（统一转换为字符串）
      // user.id 字段嵌套在 member.user.id 中
      const memberIds = new Set<string>(
        members
          .filter((member: any) => member.user && member.user.id)
          .map((member: any) => String(member.user.id))
      );

      console.log('[ShareModal] Project member ids:', Array.from(memberIds));
      setProjectMemberIds(memberIds);
    } catch (error) {
      console.error('[ShareModal] Error fetching project members:', error);
      // 失败时清空项目成员列表
      setProjectMemberIds(new Set());
    }
  }, [projectId]);

  // Fetch users from API - separated into two functions to avoid circular dependencies
  const loadUsersFromAPI = useCallback(async () => {
    if (hasLoadedUsersRef.current) {
      return;
    }
    try {
      setIsLoading(true);
      const url = '/api/shared-users';
      const response = await apiGet<any>(url);
      const data = response?.data || response || {};
      if (data) {
        // 排除当前登录用户
        const allUsers = data.users || [];
        const filteredOutCurrentUser = currentUserId
          ? allUsers.filter((user: User) => {
              // 确保类型一致（都转为字符串比较）
              const userId = String(user.id);
              const currentId = String(currentUserId);
              const shouldKeep = userId !== currentId;
              return shouldKeep;
            })
          : allUsers;

        // 处理用户数据，确保有 isDeveloper 字段（isProjectMember 不再需要，通过 email 匹配判断）
        const processedUsers = filteredOutCurrentUser.map((user: any) => ({
          ...user,
          isDeveloper: user.isDeveloper || false,
        }));

        setUsers(processedUsers);
        setFilteredUsers(processedUsers);
        hasLoadedUsersRef.current = true;
      } else {
        showToast(t('errors.failedToFetchUserList'), 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast(t('errors.failedToFetchUserList'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, t]);
  // Filter users based on search query
  const filterUsers = useCallback(
    (search: string) => {
      const trimmedSearch = search.trim().toLowerCase();
      if (trimmedSearch) {
        setFilteredUsers(
          users.filter(
            user =>
              user.name.toLowerCase().includes(trimmedSearch) ||
              user.email.toLowerCase().includes(trimmedSearch)
          )
        );
      } else {
        setFilteredUsers(users);
      }
    },
    [users]
  );
  // Handle search with debounce
  useEffect(() => {
    if (!hasLoadedUsersRef.current) {
      return;
    }
    const debounceTimer = setTimeout(() => {
      filterUsers(searchQuery);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterUsers]);
  // Initial load when modal opens
  useEffect(() => {
    if (isOpen) {
      // 重置加载状态，允许重新加载
      hasLoadedUsersRef.current = false;
      setSelectedUsers(new Set());
      setSearchQuery('');
      setSharedDescription('');
      setProjectMemberIds(new Set());

      // 确保在获取 userInfo 后再加载
      if (currentUserId) {
        loadUsersFromAPI();
        // 如果有 projectId，也加载项目成员
        if (projectId) {
          loadProjectMembers();
        }
      } else {
        // 如果 currentUserId 还没有，稍后再试
        setTimeout(() => {
          hasLoadedUsersRef.current = false;
          loadUsersFromAPI();
          if (projectId) {
            loadProjectMembers();
          }
        }, 100);
      }
      // Focus search input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUserId, projectId]); // 添加 currentUserId 和 projectId 作为依赖
  // Handle user selection
  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);
  // Handle select all
  const handleSelectAll = useCallback(() => {
    const isAllSelected = filteredUsers.every(user =>
      selectedUsers.has(user.id)
    );
    if (isAllSelected) {
      // Deselect all visible users
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all visible users
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        filteredUsers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers]);

  // Handle select project members
  const handleSelectProjectMembers = useCallback(() => {
    if (!projectId || projectMemberIds.size === 0) return;

    // 通过 id 匹配找出项目成员（统一转换为字符串比较）
    const projectMembers = filteredUsers.filter(user =>
      projectMemberIds.has(String(user.id))
    );

    const isAllProjectMembersSelected = projectMembers.every(user =>
      selectedUsers.has(user.id)
    );

    if (isAllProjectMembersSelected) {
      // Deselect all project members
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        projectMembers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all project members
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        projectMembers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers, projectId, projectMemberIds]);

  // Handle select developers
  const handleSelectDevelopers = useCallback(() => {
    const developers = filteredUsers.filter(user => user.isDeveloper);
    const isAllDevelopersSelected = developers.every(user =>
      selectedUsers.has(user.id)
    );

    if (isAllDevelopersSelected) {
      // Deselect all developers
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        developers.forEach(user => newSet.delete(user.id));
        return newSet;
      });
    } else {
      // Select all developers
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        developers.forEach(user => newSet.add(user.id));
        return newSet;
      });
    }
  }, [filteredUsers, selectedUsers]);
  // Handle share
  const handleShare = async () => {
    if (selectedUsers.size === 0) {
      showToast(t('share.selectUsersToShare'), 'error');
      return;
    }
    try {
      setIsSharing(true);
      // 直接使用 selectedUsers (已经是 userId Set)
      const userIds = Array.from(selectedUsers);
      const res = await apiPost(
        `/api/ai/conversations/${conversationId}/share`,
        {
          userIds, // 改为传 userIds
          sharedDescription,
        }
      );
      if (res.code === 200) {
        showToast(t('toast.shareSuccess'), 'success');
        onClose();
      }
    } catch (error) {
      console.error('Error sharing conversation:', error);
      showToast(t('toast.shareFailed'), 'error');
    } finally {
      setIsSharing(false);
    }
  };
  // Get user avatar initials
  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };
  // Generate avatar background color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-red-400',
      'bg-orange-400',
      'bg-yellow-400',
      'bg-green-400',
      'bg-blue-400',
      'bg-indigo-400',
      'bg-purple-400',
      'bg-pink-400',
      'bg-teal-400',
      'bg-cyan-400',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };
  if (!isOpen) return null;
  // 计算"全部"按钮的三态
  const allSelectedCount = filteredUsers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllSelected =
    filteredUsers.length > 0 && allSelectedCount === filteredUsers.length;
  const isSomeSelected =
    allSelectedCount > 0 && allSelectedCount < filteredUsers.length;

  // 计算"项目成员"按钮的三态
  const projectMembers = filteredUsers.filter(user =>
    projectMemberIds.has(String(user.id))
  );
  const pmSelectedCount = projectMembers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllProjectMembersSelected =
    projectMembers.length > 0 && pmSelectedCount === projectMembers.length;
  const isSomeProjectMembersSelected =
    pmSelectedCount > 0 && pmSelectedCount < projectMembers.length;

  // 计算"开发者"按钮的三态
  const developers = filteredUsers.filter(user => user.isDeveloper);
  const devSelectedCount = developers.filter(user =>
    selectedUsers.has(user.id)
  ).length;
  const isAllDevelopersSelected =
    developers.length > 0 && devSelectedCount === developers.length;
  const isSomeDevelopersSelected =
    devSelectedCount > 0 && devSelectedCount < developers.length;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader>
          <DialogTitle>{t('share.shareConversation')}</DialogTitle>
          <p
            className="text-sm text-gray-600 truncate"
            title={conversationTitle}
          >
            {conversationTitle}
          </p>
        </DialogHeader>
        {/* Search */}
        <div className="px-6 py-2 border-b">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('form.searchMembers')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-1 mb-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        {/* Select Options */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-2 border-b">
            <div className="flex gap-2">
              {/* All Button */}
              <button
                onClick={handleSelectAll}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors hover:bg-gray-100 border border-gray-300"
              >
                <div
                  className={cn(
                    'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    isAllSelected || isSomeSelected
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  )}
                >
                  {isAllSelected && <Check size={10} className="text-white" />}
                  {isSomeSelected && <Minus size={10} className="text-white" />}
                </div>
                {t('share.all')}
              </button>

              {/* Project Members Button */}
              <button
                onClick={handleSelectProjectMembers}
                disabled={!projectId || projectMemberIds.size === 0}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors border border-gray-300 ${
                  !projectId || projectMemberIds.size === 0
                    ? 'opacity-50 cursor-not-allowed bg-gray-50'
                    : 'hover:bg-gray-100'
                }`}
                title={
                  !projectId
                    ? t('share.notInProject') || 'Not in a project'
                    : ''
                }
              >
                <div
                  className={cn(
                    'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    isAllProjectMembersSelected || isSomeProjectMembersSelected
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  )}
                >
                  {isAllProjectMembersSelected && (
                    <Check size={10} className="text-white" />
                  )}
                  {isSomeProjectMembersSelected && (
                    <Minus size={10} className="text-white" />
                  )}
                </div>
                {t('share.projectMembers') || 'Project Members'}
              </button>

              {/* Developers Button */}
              <button
                onClick={handleSelectDevelopers}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors hover:bg-gray-100 border border-gray-300"
              >
                <div
                  className={cn(
                    'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    isAllDevelopersSelected || isSomeDevelopersSelected
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300'
                  )}
                >
                  {isAllDevelopersSelected && (
                    <Check size={10} className="text-white" />
                  )}
                  {isSomeDevelopersSelected && (
                    <Minus size={10} className="text-white" />
                  )}
                </div>
                {t('share.developers') || 'Developers'}
              </button>
            </div>
          </div>
        )}
        {/* Users list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="px-6 py-3 text-center text-gray-500">
              {t('form.loadingUsers')}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-6 py-3 text-center text-gray-500">
              {searchQuery
                ? t('form.noMatchingUsers')
                : t('form.noUsersAvailable')}
            </div>
          ) : (
            <div className="px-6 py-2 space-y-2">
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => toggleUserSelection(user.id)}
                  className="flex items-center gap-3 pb-2 border-b w-full text-left hover:bg-gray-50 rounded-md transition-colors"
                >
                  {/* Avatar */}
                  <div
                    className={cn(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                      getAvatarColor(user.name)
                    )}
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getAvatarInitials(user.name)
                    )}
                  </div>
                  {/* User info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                      selectedUsers.has(user.id)
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {selectedUsers.has(user.id) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Description */}
        <div className="px-6 py-3 border-b">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('form.description')}
          </label>
          <textarea
            value={sharedDescription}
            onChange={e => setSharedDescription(e.target.value)}
            placeholder={t('form.addDescription')}
            rows={2}
            disabled={isSharing}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          />
        </div>
        {/* Footer */}
        <DialogFooter className="flex-row justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedUsers.size === 0
              ? t('share.noUsersSelected')
              : selectedUsers.size === 1
                ? t('share.userSelected')
                : t('share.usersSelected', { count: selectedUsers.size })}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSharing}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleShare}
              disabled={isSharing || selectedUsers.size === 0}
            >
              {isSharing ? t('share.sharing') : t('common.ok')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
